import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { StatefulService } from '@app/service/stateful.service';
import { LenderService } from '@shared/service/lender/lender.service';
import { FlowInstance, FlowService } from '@workflow/class/flow.service';
import { CustomModal } from '@workflow/dynamic-render/page/shared/core-custom-page.decorator';
import { CorePageBaseComponent } from '@workflow/dynamic-render/page/shared/core-page-base.component';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';

import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { ApiResult } from '@workflow/ecp-workflow/dynamic-app/service/application-api/application-api.types';
import { L360Service } from '@workflow/ecp-workflow/dynamic-app/service/l360/l360.service';
import { PickListTypes } from '@workflow/ecp-workflow/dynamic-app/types/picklist';
import { getFlowInfo, FlowInfo } from '@workflow/helper/flow-utility/flow-utility';
import { WorkflowPageNavigationService } from '@workflow/service/workflow-page-navigation/workflow-page-navigation.service';
import { WorkflowService } from '@workflow/service/workflow/workflow.service';
import { WorkflowWidgetEventServiceService } from '@workflow/widget/workflow-widget-event.service';

@CustomModal('ola-confirm-loan-group')
@Component({
  selector: 'ecp-ola-confirm-loan-group',
  templateUrl: './confirm-loan-group.component.html'
})
export class ConfirmLoanGroupComponent extends CorePageBaseComponent implements OnInit {
  private readonly statefulService = inject(StatefulService);

  constructor(
    public workflowPageNavigationService: WorkflowPageNavigationService,
    public readonly widgetEventService: WorkflowWidgetEventServiceService,
    public readonly adminPortalService: AdminPortalFeaturesService,
    private readonly router: Router,
    public readonly lenderService: LenderService,
    private readonly userService: UserService,
    private readonly workflowService: WorkflowService,
    public readonly l360ApiService: L360Service,
    private readonly flowService: FlowService
  ) {
    super(workflowPageNavigationService, widgetEventService);
  }
  public confirmLoanGroupChange(): void {
    this.statefulService.wrap('ss:showLoanGroupChangeModal').value = false;
    const loanGroup = this.statefulService.wrap('ss:ola-new-loan-group').valueString;
    const loanType = this.statefulService.wrap('ss:ola-new-loan-type').valueString;
    const newUITemplate = this.statefulService.wrap('ss:ola-new-ui-template').value;

    this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName').value = loanGroup;
    this.statefulService.wrap('ola:olaApplicationInformation/loanProductName').value = loanType;
    // Load Loan Purpose Dropdown
    this.loadLoanPurposeDropdown(loanGroup, loanType);
    const oldUITempalte = this.statefulService.wrap('ola:olaApplicationInformation/uiTemplateName').valueString;
    this.statefulService.wrap('ola:olaApplicationInformation/uiTemplateName').value = newUITemplate;
    this.statefulService.wrap('ss:is-loangroup-got-changed').value = true;
    this.statefulService.wrap('ss:is-loangroup-got-changed-for-make').value = true;
    const templates = this.adminPortalService?.config?.olaConfiguration?.masterUITemplates;
    const template = templates.filter((t) => t.templateName.toLowerCase() === oldUITempalte.toLowerCase())[0];
    const flowPath = template?.path;

    if (flowPath) {
      const flow = this.flowService.createFlow(flowPath?.toString());
      const eligiblity = flow.allPages.find((t) => t.id === 'ola-eligibility');
      this.statefulService.wrap('ola:skipEligibility').value = !eligiblity?.visible;
    }

    this.statefulService.wrap('ss:ola-new-loan-group').value = '';
    this.statefulService.wrap('ss:ola-new-loan-type').value = '';
    this.statefulService.wrap('ss:ola-new-ui-template').value = '';
    const isEligibilityCompleted = this.statefulService.wrap('ola:olaApplicationInformation/isEligibilityCompleted').value;
    if (this.flow.config.path && this.userService && this.userService.isAuthenticated && isEligibilityCompleted !== false) {
      this.statefulService.wrap('ola:consumerId').load(() => {
        this.statefulService.wrap(`ola:borrowerInfo/autoFillOption`).value = null;
        this.statefulService.wrap(`ola:borrowerInfo/autoFillStatus`).value = false;
        this.statefulService.wrap('ola:borrowerInfo/isPrefilled').value = false;
        this.statefulService.wrap(`ola:borrowerInfo/autoFillDateOfBirthValid`).value = false;
        this.statefulService.wrap('ola:borrowerInfo/enableAutoFillFirstName').value = true;
        this.statefulService.wrap('ola:borrowerInfo/enableAutoFillLastName').value = true;
        this.statefulService.wrap('ola:borrowerInfo/enableAutoFillMobileNumber').value = true;
        const hasCoborrower = this.statefulService.wrap(`ola:borrowerInfo/hasCoBorrower`).value;
        if (hasCoborrower) {
          this.statefulService.wrap(`ola:coBorrowers/0/borrowerInfo/autoFillOption`).value = null;
          this.statefulService.wrap(`ola:coBorrowers/0/borrowerInfo/autoFillStatus`).value = false;
          this.statefulService.wrap('ola:coBorrowers/0/borrowerInfo/isPrefilled').value = false;
          this.statefulService.wrap(`ola:coBorrowers/0/borrowerInfo/autoFillDateOfBirthValid`).value = false;
          this.statefulService.wrap('ola:coBorrowers/0/borrowerInfo/enableAutoFillFirstName').value = true;
          this.statefulService.wrap('ola:coBorrowers/0/borrowerInfo/enableAutoFillLastName').value = true;
          this.statefulService.wrap('ola:coBorrowers/0/borrowerInfo/enableAutoFillMobileNumber').value = true;
        }

        this.workflowService.currentFlow.pages.forEach((p) => {
          this.workflowPageNavigationService.updateNavState(p);
        });
        if (oldUITempalte && newUITemplate && oldUITempalte !== newUITemplate) {
          this.router.navigate([`app/${flowPath}/ola-consumer-loan-type`]);
        }
      });
    }

    if (this.workflowService.findNextPage()) {
      this.workflowPageNavigationService.navigateNextPage();
    } else {
      this.redirectToLoanDetail(flowPath);
    }
  }

  redirectToLoanDetail(flowPath: string) {
    const flow = this.flowService.createFlow(flowPath?.toString());
    const flowInfo = getFlowInfo(flow);
    const path = this.getOLAFirstInvalidPage(flow, flowInfo, flowPath);
    this.router.navigate(['app/' + path]);
  }

  private getOLAFirstInvalidPage(flow: FlowInstance, flowInfo: FlowInfo, workflowPrefix: string): string {
    let objFirstVisiblePage = 'ola/ola-consumer-loan-type';
    if (flow.id.toLowerCase() === workflowPrefix) {
      const visiblePages = flow.pages.filter((t) => t.visible && t.id !== 'ola-consumer-loan-type');
      if (visiblePages && visiblePages.length > 0) {
        objFirstVisiblePage = `${flowInfo.configPath}/${visiblePages[0].path}`;
      }
    }
    return objFirstVisiblePage;
  }

  public cancelLoanGroupChangeModal(): void {
    this.statefulService.wrap('ss:showLoanGroupChangeModal').value = false;
    this.statefulService.wrap('ss:ola-loan-group-confirm-popup-close').value = new Date().toTimeString();
    this.statefulService.wrap('ss:is-loangroup-got-changed').value = false;
  }

  /**
   * Load loan purpose dropdown
   * @param loanGroup
   * @param loanProduct
   */
  private loadLoanPurposeDropdown(loanGroup: string, loanProduct: string): void {
    const selectedpickListType = 'LoanPurpose';
    const selectedLoanPurposeSubkey = this.getLoanProduct(loanGroup, loanProduct)?.lenderLoanProduct;
    if (this.l360ApiService.getPickListTypes(selectedpickListType, selectedLoanPurposeSubkey).length === 0) {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      this.l360ApiService.picklistLoading['LoanPurpose'] = true;
      this.l360ApiService.getPickListItems(selectedpickListType, selectedLoanPurposeSubkey).subscribe((data: ApiResult<PickListTypes>) => {
        if (data.isSuccess) {
          // set LoanPurposePickListData
          this.l360ApiService.LoanPurposePickListData = data?.data;
        }
        // eslint-disable-next-line @typescript-eslint/dot-notation
        this.l360ApiService.picklistLoading['LoanPurpose'] = false;
      });
    }
  }

  /**
   * Get the selected loan group from lender config
   * @param loanGroup
   * @returns selected loan group
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getLoanGroup(loanGroup: string): any {
    if (
      loanGroup &&
      this.adminPortalService?.config &&
      this.adminPortalService?.config?.olaConfiguration &&
      this.adminPortalService?.config?.olaConfiguration.consumerLoanGroups
    ) {
      const loanGroupObj = this.adminPortalService?.config?.olaConfiguration.consumerLoanGroups.filter(
        (t) => t?.loanGroup?.toLowerCase() === loanGroup?.toLowerCase()
      );
      return loanGroupObj && loanGroupObj.length > 0 ? loanGroupObj[0] : [];
    }

    return undefined;
  }

  /**
   * Get the selected loan product from lender config
   * @param loanGroup
   * @param loanProduct
   * @returns selected loan product
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getLoanProduct(loanGroup: string, loanProduct: string): any {
    const loanGroupObj = this.getLoanGroup(loanGroup);
    if (loanGroupObj?.loanProducts) {
      const loanProductObj = loanGroupObj.loanProducts.filter(
        (t) => !loanGroupObj.isLoanGroupHasProduct || (loanProduct && t?.productName?.toLowerCase() === loanProduct?.toLowerCase())
      );
      return loanProductObj && loanProductObj.length > 0 ? loanProductObj[0] : [];
    }
    return undefined;
  }
}
